import React from "react";
import { Helmet } from "react-helmet";

const SITE_URL = "https://www.edyn.care";
const DEFAULT_IMAGE = `${SITE_URL}/assets/marketing-site/images/open-graph-img.png`;

export default function Seo({
	title,
	description,
	path = "/",
	image = DEFAULT_IMAGE,
	service,
	breadcrumbs = [],
	children,
}) {
	const canonical = `${SITE_URL}${path}`;
	const organisationSchema = {
		"@context": "https://schema.org",
		"@type": "Organization",
		"@id": `${SITE_URL}/#organisation`,
		name: "Edyn Care",
		legalName: "Age Abode Limited",
		url: SITE_URL,
		logo: {
			"@type": "ImageObject",
			url: `${SITE_URL}/assets/marketing-site/images/logo.png`,
		},
		telephone: "+442039709900",
		email: "hello@edyn.care",
		address: {
			"@type": "PostalAddress",
			streetAddress: "Shoreditch Exchange, Senna Building, Gorsuch Place",
			addressLocality: "London",
			postalCode: "E2 8JF",
			addressCountry: "GB",
		},
		contactPoint: {
			"@type": "ContactPoint",
			telephone: "+442039709900",
			contactType: "customer care",
			areaServed: "GB",
			availableLanguage: "English",
		},
		areaServed: {
			"@type": "Country",
			name: "England",
		},
		sameAs: [
			"https://uk.trustpilot.com/review/edyn.care",
			"https://www.facebook.com/EdynCare/",
			"https://twitter.com/EdynCare",
			"https://www.cqc.org.uk/location/1-5584086807",
		],
	};
	const websiteSchema = {
		"@context": "https://schema.org",
		"@type": "WebSite",
		"@id": `${SITE_URL}/#website`,
		name: "Edyn Care",
		url: SITE_URL,
		publisher: { "@id": `${SITE_URL}/#organisation` },
	};
	const serviceSchema = service && {
		"@context": "https://schema.org",
		"@type": "Service",
		"@id": `${canonical}#service`,
		name: service.name,
		serviceType: service.serviceType || "Fully managed live-in care",
		description,
		url: canonical,
		provider: { "@id": `${SITE_URL}/#organisation` },
		areaServed: (service.areaServed || ["England"]).map((name) => ({
			"@type": name === "England" ? "Country" : "AdministrativeArea",
			name,
		})),
	};
	const breadcrumbSchema = breadcrumbs.length > 0 && {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: breadcrumbs.map(({ name, path: itemPath }, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name,
			item: `${SITE_URL}${itemPath}`,
		})),
	};
	const schemas = [
		organisationSchema,
		websiteSchema,
		serviceSchema,
		breadcrumbSchema,
	].filter(Boolean);

	return (
		<Helmet htmlAttributes={{ lang: "en-GB" }}>
			<meta charSet="utf-8" />
			<meta name="viewport" content="width=device-width, initial-scale=1" />
			<title>{title}</title>
			<meta name="description" content={description} />
			<link rel="canonical" href={canonical} />
			<meta property="og:type" content="website" />
			<meta property="og:site_name" content="Edyn Care" />
			<meta property="og:title" content={title} />
			<meta property="og:description" content={description} />
			<meta property="og:url" content={canonical} />
			<meta property="og:image" content={image} />
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={title} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={image} />
			{schemas.map((schema) => (
				<script key={schema["@id"] || schema["@type"]} type="application/ld+json">
					{JSON.stringify(schema)}
				</script>
			))}
			{children}
		</Helmet>
	);
}
